import React, { useMemo } from 'react'
import { type Row, useExpanded, useTable } from 'react-table'
import '../../table/organizations-table.css'
import { Button } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import ModeEditIcon from '@mui/icons-material/ModeEdit'
import DeleteIcon from '@mui/icons-material/Delete'
import { type ExistingSlo } from 'services/organization/slos/slosService'
import { formatDate } from 'components'

interface SloTableProps {
  slos: ExistingSlo[]
  orderField: string
  orderDirection: string
  openEdit: (slo: ExistingSlo) => void
  openDelete: (slo: ExistingSlo) => void
  updateOrdering: (field: string, direction: string) => void
}

export const SlosTable: React.FC<SloTableProps> = ({ slos, orderField, orderDirection, updateOrdering, openEdit, openDelete }) => {
  const navigate = useNavigate()

  const columns: any = useMemo(
    () => [
      { Header: 'ID', accessor: 'id', order_field: 'id' },
      { Header: 'Country', accessor: 'country.country_desc', order_field: 'country' },
      { Header: 'Org. Type', accessor: 'orgType.typeDescShort', order_field: 'type' },
      { Header: 'Number of Days', accessor: 'days', order_field: 'days' },
      {
        Header: 'Last Updated',
        accessor: (row: ExistingSlo) => { return formatDate(row.lastUpdated ?? '') },
        order_field: 'lastUpdated',
        Cell: ({ row }: { row: Row<ExistingSlo> }) => (
              <div id={`tooltip-updated-${row.id}`} title={`Updated By: ${row.original.updatedBy ?? 'Unknown'}`}>
                  {formatDate(row.original.lastUpdated ?? '')}
              </div>
        )
      },
      {
        Header: 'Edit',
        id: 'edit',
        Cell: ({ row }: { row: Row<ExistingSlo> }) => (
                    <Button
                        id={`edit-${(row.values.id as string)}`}
                        variant="contained"
                        size='medium'
                        onClick={() => {
                          openEdit(row.original)
                        }}
                    >
                        <ModeEditIcon />
                    </Button>
        )
      },
      {
        Header: 'Delete',
        id: 'delete',
        Cell: ({ row }: { row: Row<ExistingSlo> }) => (
                <Button
                    id={`delete-${(row.values.id as string)}`}
                    variant="contained"
                    color='secondary'
                    size='medium'
                    onClick={() => {
                      openDelete(row.original)
                    }}
                >
                    <DeleteIcon />
                </Button>
        )
      }
    ],
    [navigate]
  )

  const tableData = useMemo(() => slos, [slos])

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow
  } = useTable<ExistingSlo>(
    {
      columns,
      data: tableData
    },
    useExpanded
  )

  function handleHeaderClick (column: any): void {
    const direction = orderField === column.order_field ? (orderDirection === 'ASC' ? 'DESC' : 'ASC') : orderDirection
    updateOrdering(column.order_field, direction)
  }

  return (
      <React.Fragment>
          {slos.length === 0
            ? (
              <p>No SLOs to show.</p>
              )
            : (
        <table {...getTableProps()} className="table">
            <thead>
            {headerGroups.map((headerGroup, index) => {
              return (
                    <tr {...headerGroup.getHeaderGroupProps()} key={index}>
                        {headerGroup.headers.map((column: any) => (
                            <th
                                {...column.getHeaderProps}
                                key={column.id}
                                onClick={() => { if (column.id !== 'edit' && column.id !== 'delete') handleHeaderClick(column) }}
                                className={column.id === 'edit' || column.id === 'delete' ? 'noclick-column-header' : '' }
                            >
                                <div style={{ display: 'flex', flexDirection: 'row' }}>
                                {column.render('Header')}
                                <span>
                                    {orderField === column.order_field ? (orderDirection === 'ASC' ? ' ↓' : ' ↑') : ''}
                                </span>
                                </div>
                            </th>))}
                    </tr>
              )
            })}
            </thead>
            <tbody {...getTableBodyProps()}>
            {rows.map((row) => {
              prepareRow(row)
              return (
                    <tr {...row.getRowProps()} key={row.id}>
                        {row.cells.map((cell) => (
                            <td {...cell.getCellProps()} key={cell.column.id}>{cell.render('Cell')}</td>
                        ))}
                    </tr>
              )
            })}
            </tbody>
        </table>
              )}
      </React.Fragment>
  )
}
